import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import BannerUploader from "./BannerUploader";
import { useTranslation } from "react-i18next";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const useStyles = makeStyles((theme) => ({
	modal: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: 400,
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[5],
		padding: theme.spacing(4),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
		maxHeight: "90vh",
		overflow: "auto",
		[theme.breakpoints.down("sm")]: {
			// Медиа-запрос для экранов меньше small (600px)
			width: "90%", // Устанавливаем максимальную ширину для маленьких экранов
			top: 0,
			left: 0,
			right: 0,
			transform: "translate(0, 0)",
			padding: "16px",
		},
	},
	modalButton: {
		marginTop: theme.spacing(2),
	},
	formControl: {
		width: "100%",
	},
	labelList: {
		marginBottom: theme.spacing(2),
		overflowY: "scroll", // Прокрутка вн
	},
	labelItem: {
		display: "flex",
		justifyContent: "space-between",
		padding: theme.spacing(1),
		backgroundColor: theme.palette.grey[200],
		borderRadius: theme.shape.borderRadius,
		marginBottom: "1px",
	},
	labelRemoveButton: {
		cursor: "pointer",
		color: theme.palette.error.main,
	},
}));

const LabelModal = ({
	id,
	open,
	onClose,
	onSave,
	initialData,
	uploadImage,
	handleDeleteImg,
}) => {
	const classes = useStyles();
	const [labelName, setLabelName] = useState("");
	const [position, setPosition] = useState(1);
	const [productsIds, setProductsIds] = useState([]);
	const [currentId, setCurrentId] = useState("");
	const [image, setImageURL] = useState();
	const [pendingImage, setPendingImage] = useState(null);
	const [errorMes, setErrorMes] = useState("");

	const { t } = useTranslation();

	const [bannerType, setBannerType] = useState("image");
	const [textValue, setTextValue] = useState("");

	useEffect(() => {
		if (initialData) {
			// Если редактируем существующий
			setPosition(initialData.position || 1);
			setProductsIds(initialData.productsIds || []);
			setImageURL(initialData.image || "");

			// Определяем тип баннера на основе входящих данных
			const type =
				initialData.text && initialData.text.length > 0 ? "text" : "image";
			setBannerType(type);

			if (type === "text" && initialData.text) {
				setTextValue(initialData.text);
			}
		} else {
			// Если создаем новый — сбрасываем форму
			clearForm();
		}
		// bannerType УДАЛЕН из зависимостей, чтобы не сбрасывать форму при переключении
	}, [initialData]);

	const handleAddLabel = () => {
		if (currentId.trim() === "") return; // Проверка на пустой ID
		setProductsIds([...productsIds, currentId]);
		setCurrentId(""); // Очистка поля ввода после добавления
	};

	const clearForm = () => {
		setPosition(1);
		setProductsIds([]);
		setCurrentId("");
		setImageURL("");
		setPendingImage(null);
		setBannerType("image");
		setTextValue("");
		setErrorMes("");
	};

	const handleRemoveLabel = (id) => {
		setProductsIds(productsIds.filter((label) => label !== id));
	};

	const handleImageUpload = (img) => {
		setPendingImage(img);
		setErrorMes(""); // Сохраняем выбранное изображение, но не загружаем его
	};

	const handleSave = async () => {
		const isActive =
			initialData?.isActive !== undefined ? initialData?.isActive : true;

		// Создаем базовый объект
		const payload = {
			tagName: "",
			isActive,
			position,
			productsIds,
		};

		if (bannerType === "image") {
			let finalImageURL = image;

			if (!image && !pendingImage) {
				setErrorMes(t("labels.requiredFieldImg"));
				return;
			}

			if (pendingImage) {
				finalImageURL = await uploadImage(pendingImage);
			}

			// Явно фиксируем: картинка есть, текста НЕТ
			payload.image = finalImageURL;
			payload.text = "";
		} else if (bannerType === "text") {
			if (!textValue.trim()) {
				setErrorMes("Введіть текст");
				return;
			}

			const formatted = `${textValue}`;

			// Явно фиксируем: текста есть, картинки НЕТ
			payload.text = formatted;
			payload.image = "";
		}

		// Отправляем ОДИН сформированный объект
		// console.log("Saving payload:", payload); // Проверьте в консоли, что летит в базу
		onSave(payload);

		clearForm();
		onClose();
	};

	const setMinHeigth = (amount) => {
		const heightBLock = 65;
		if (amount > 3) {
			return 3 * heightBLock + "px";
		}
		return amount * heightBLock + "px";
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box className={classes.modal}>
				<Typography variant="h6">
					{initialData ? t("labels.editLabel") : t("labels.createNew")}
				</Typography>

				<FormControl className={classes.formControl}>
					<InputLabel>{t("labels.position")}</InputLabel>
					<Select
						value={position}
						onChange={(e) => setPosition(e.target.value)}
					>
						{[1, 2, 3, 4].map((pos) => (
							<MenuItem key={pos} value={pos}>
								{pos}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					label={t("menu.enterProduct")}
					value={currentId}
					onChange={(e) => setCurrentId(e.target.value)}
					fullWidth
				/>
				<Button variant="contained" color="primary" onClick={handleAddLabel}>
					{t("buttons.add")}
				</Button>
				<Box
					className={classes.labelList}
					style={{ minHeight: setMinHeigth(productsIds.length) }}
				>
					{productsIds.map((id, i) => (
						<Box key={id} className={classes.labelItem}>
							<Typography>{id}</Typography>
							<Button
								className={classes.labelRemoveButton}
								onClick={() => handleRemoveLabel(id)}
							>
								{t("buttons.delete")}
							</Button>
						</Box>
					))}
				</Box>

				<RadioGroup
					value={bannerType}
					onChange={(e) => setBannerType(e.target.value)}
					row
				>
					<FormControlLabel
						value="image"
						control={<Radio color="primary" />}
						label="Баннер"
					/>
					<FormControlLabel
						value="text"
						control={<Radio color="primary" />}
						label="Текст"
					/>
				</RadioGroup>

				{/* IMAGE */}
				{bannerType === "image" && (
					<BannerUploader
						image={image}
						setImageURL={setImageURL}
						handleImageUpload={handleImageUpload}
						handleDeleteImg={handleDeleteImg}
						errorMes={errorMes}
					/>
				)}

				{bannerType === "text" && (
					<TextField
						label="Текст банера"
						value={textValue}
						onChange={(e) => setTextValue(e.target.value)}
						fullWidth
					/>
				)}

				<Button
					variant="contained"
					color="primary"
					onClick={handleSave}
					className={classes.modalButton}
				>
					{initialData ? t("buttons.save") : t("buttons.create")}
				</Button>
				<Button
					variant="outlined"
					onClick={onClose}
					className={classes.modalButton}
				>
					{t("buttons.cancel")}
				</Button>
			</Box>
		</Modal>
	);
};

export default LabelModal;
